import {
  CircleCheck,
  Download,
  FileText,
  Info,
  Loader,
  Loader2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { getExperianV2Pdf } from "../../../Services/ExperianV2Pdf/ExperianV2PdfService";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { fetchProfile } from "../../../Services/profile/profileservice";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";

const ExperianV2Pdf = () => {
  const [formData, setFormData] = useState({
    mobile_no: "",
    first_name: "",
    last_name: "",
    dob: "",
    pan: "",
  });

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name) {
      return showErrorToast("First name is required");
    } else if (!formData.last_name) {
      return showErrorToast("Last name is required");
    } else if (!formData.mobile_no) {
      return showErrorToast("Mobile No. is required");
    } else if (!formData.dob) {
      return showErrorToast("Date of birth is required");
    } else if (!formData.pan) {
      return showErrorToast("Pan No. is required");
    }

    console.log("Form Submitted:", formData);
    setLoading(true);

    const payload = {
      api_id: profileData.user_id,
      api_key: profileData.api_key,
      token_id: profileData.token_id,
      ...formData,
    };

    try {
      const res = await getExperianV2Pdf(payload);

      if (res?.status?.code === 200 || res?.data?.status?.code === 200) {
        setReportData(res?.data);
        showSuccessToast(res?.message || res?.data?.message);
      } else {
        showErrorToast(
          res?.message || res?.data?.message || res?.data?.error?.message,
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!reportData?.result_pdf) {
      console.error("No PDF URL found");
      return;
    }

    const link = document.createElement("a");
    link.href = reportData.result_pdf;
    link.download = "experian_v2_report.pdf";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={"Experian V2 PDF"} />

      <div className="min-h-screen bg-gray-50 flex items-start justify-center py-6">
        <div className="w-full  bg-white rounded-xl shadow-lg px-4 py-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            Experian V2 Pdf
          </h2>
          <form
            onSubmit={handleSubmit}
            className=" grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
          >
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                placeholder="Ram"
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                placeholder="Bhandari"
              />
            </div>

            {/* Mobile Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mobile No. <span className="text-red-600">*</span>
              </label>
              <input
                type="tel"
                name="mobile_no"
                value={formData.mobile_no}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                placeholder="8191817766"
              />
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of Birth <span className="text-red-600">*</span>
              </label>
              <input
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
              />
            </div>

            {/* PAN Card */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PAN Number <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="pan"
                value={formData.pan}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition uppercase"
                placeholder="ARSPB2789E"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 flex items-center justify-center gap-2 rounded-lg transition duration-200 mt-4 shadow-md"
            >
              <FileText className={`${loading && "animate-pulse"}`} />
              {loading ? (
                <>
                  <Loader2 className="animate-spin" />
                </>
              ) : (
                "Get Report"
              )}
            </button>
          </form>

          {reportData && (
            <div className="flex justify-center border-2 rounded-2xl p-4 border-indigo-600/50 border-dashed gap-4 mt-6">
              <p className="flex text-indigo-600 items-center gap-3">
                {" "}
                <CircleCheck /> Report Available
              </p>
              <button
                onClick={handleDownload}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 active:scale-95 group"
              >
                <FileText
                  size={20}
                  className="group-hover:scale-110 transition-transform"
                />
                <span>Preview and Download</span>
                <div className="ml-1 pl-2 border-l border-indigo-400">
                  <Download size={18} />
                </div>
              </button>
            </div>
          )}

          {/* Instruction Message */}
          <div className="bg-emerald-50 border border-emerald-200  rounded-xl p-5 my-6">
            <div className="flex items-center gap-2 mb-3">
              <Info size={18} className="text-emerald-600" />
              <h3 className="text-sm font-semibold text-emerald-900 uppercase tracking-wider">
                Submission Instructions
              </h3>
            </div>

            <ul className="space-y-2 text-sm text-emerald-800">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                <span>
                  Please ensure all details match your official documents
                  (PAN/Aadhar).
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                <span>
                  Upon successful verification, your{" "}
                  <strong>Experian v2 Credit Report</strong> will be generated
                  as a PDF.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExperianV2Pdf;
